import { z } from 'zod';
// ── Primitive schemas ─────────────────────────────────────────────────────────
/**
 * A stage. `connectionId` + `branch` are authoritative; the env/alias/url fields
 * are a cached display projection (refreshed from the connection on save).
 */
export const pipelineStageSchema = z.object({
    id: z.string().min(1, 'Stage id is required'),
    name: z.string().min(1, 'Stage name is required'),
    connectionId: z.string().min(1, 'connectionId is required'),
    branch: z.string().min(1, 'branch is required'),
    // cached display projection — optional, not authoritative
    envType: z.enum(['production', 'sandbox', 'developer', 'scratch']).optional(),
    alias: z.string().min(1).optional(),
    sfdxAlias: z.string().min(1).optional(),
    instanceUrl: z.string().url('instanceUrl must be a valid URL').optional(),
});
/** A transition between consecutive stages. Config fields are display-only in Phase 1. */
export const pipelineTransitionSchema = z.object({
    id: z.string().min(1, 'Transition id is required'),
    from: z.string().min(1, 'from stage id is required'),
    to: z.string().min(1, 'to stage id is required'),
    mode: z.enum(['manual', 'auto']),
    gate: z.boolean(),
    approval: z.enum(['none', 'required']),
});
// ── API-level schemas ─────────────────────────────────────────────────────────
/** Body shape for POST /api/pipelines */
export const createPipelineSchema = z.object({
    name: z.string().min(1, 'Pipeline name is required').max(100),
    stages: z.array(pipelineStageSchema),
    transitions: z.array(pipelineTransitionSchema),
    repo: z.string().min(1).optional(), // "owner/name"
    gitConnectionId: z.string().min(1).optional(),
    author: z.string().min(1).optional(),
});
/** Body shape for PUT /api/pipelines/:id (full replace) */
export const updatePipelineSchema = createPipelineSchema;
/** Full stored config (used in response shapes). */
export const pipelineConfigSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    projectId: z.string().min(1),
    stages: z.array(pipelineStageSchema),
    transitions: z.array(pipelineTransitionSchema),
    repo: z.string().min(1).optional(),
    gitConnectionId: z.string().min(1).optional(),
    author: z.string().min(1).optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
// ── Promotion (Phase 2) ─────────────────────────────────────────────────────
/** Body shape for POST /api/pipelines/:slug/promote */
export const promoteRequestSchema = z.object({
    transitionId: z.string().min(1, 'transitionId is required'),
    setSlug: z.string().min(1, 'setSlug is required'),
    setVersion: z.number().int().positive(),
    driftAck: z.boolean().optional(), // re-submit with true to override drift
    /** Re-submit with true to promote despite unmet dependencies in the target (records
     *  `dependencyOverride`). The gate lists what is missing; this is the operator saying
     *  "deploy without them anyway" — a decision worth having a name and an audit trail. */
    dependencyAck: z.boolean().optional(),
    /**
     * Carry REMOVALS as well as additions: on merge, delete target records that sit inside a window this
     * set is authoritative over and no longer carries (an excluded picklist value someone re-included).
     *
     * Recorded on the promotion and STATED IN THE PR BODY, because the deletion happens on merge — so the
     * person who merges is the person who consents, and they can see what it will remove before they do.
     */
    deleteRemovedRecords: z.boolean().optional(),
    author: z.string().min(1).optional(),
    /** Git-backed ARM config-data objects to include in the promote PR — their committed
     *  data/arm/<object>.json files are read off the source branch and carried into the PR,
     *  so they merge to the target branch and import on merge (the ARM half of a promotion). */
    armConfigDataObjects: z.array(z.string().min(1)).optional(),
    /** Back-promote only: before carrying the ARM tree back, RE-CAPTURE the origin (downstream) org's
     *  current state for the set's roots onto the set branch — so a change made directly in the downstream
     *  org flows back, rather than a stale committed tree. Ignored on a forward promote. */
    recaptureFromOrigin: z.boolean().optional(),
});
/**
 * Body shape for POST /api/pipelines/:slug/back-promote — a single backward hop.
 * Same fields as a forward promote; the server runs the transition in REVERSE
 * (origin = transition.to, target = transition.from). Fan-out / cascade extend this later.
 */
export const backPromoteRequestSchema = z.object({
    transitionId: z.string().min(1, 'transitionId is required'),
    setSlug: z.string().min(1, 'setSlug is required'),
    setVersion: z.number().int().positive(),
    driftAck: z.boolean().optional(),
    dependencyAck: z.boolean().optional(),
    author: z.string().min(1).optional(),
    armConfigDataObjects: z.array(z.string().min(1)).optional(),
    /** Re-capture the origin (downstream) org's current state onto the set branch before carrying it
     *  back — so a change made directly in the downstream org is what flows back. See promoteRequestSchema. */
    recaptureFromOrigin: z.boolean().optional(),
});
/**
 * Body for POST /api/pipelines/:slug/back-promote/fanout — fan a set out to an origin
 * node's FEEDERS (derived from the graph). `targets` optionally restricts to a subset
 * (per-target opt-out). Each target runs an independent governed hop under one cascadeId.
 */
export const backPromoteFanOutRequestSchema = z.object({
    originStageId: z.string().min(1, 'originStageId is required'),
    setSlug: z.string().min(1, 'setSlug is required'),
    setVersion: z.number().int().positive(),
    targets: z.array(z.string().min(1)).optional(),
    driftAck: z.boolean().optional(),
    author: z.string().min(1).optional(),
});
/**
 * Body for POST /api/pipelines/:slug/back-promote/cascade — propagate a set DOWN the
 * whole reverse tree from an origin (origin → uat → sit → {devs}), one governed hop at
 * a time. Pauses on a hard conflict; re-invoking resumes (skip-identical no-ops done hops).
 */
export const backPromoteCascadeRequestSchema = z.object({
    originStageId: z.string().min(1, 'originStageId is required'),
    setSlug: z.string().min(1, 'setSlug is required'),
    setVersion: z.number().int().positive(),
    driftAck: z.boolean().optional(),
    author: z.string().min(1).optional(),
});
//# sourceMappingURL=pipeline.js.map